

import React, { useState, useEffect, useRef } from 'react';
import { getMedia, uploadMedia, updateMedia, deleteMedia } from '../../utils/api';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
const MediaManager = () => {
    const [media, setMedia] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        file_url: '',
        media_type: 'image',
        category: '',
        is_featured: false,
        selectedFile: null,
        filePreview: null
    });

    const fileInputRef = useRef(null);
    const FILE_SERVER_URL = process.env.REACT_APP_FILE_SERVER_URL || 'https://nkumbufileserver-production.up.railway.app';

    useEffect(() => {
        loadMedia();
    }, []);

    const loadMedia = async () => {
        try {
            setLoading(true);
            const data = await getMedia();
            
            if (Array.isArray(data)) {
                setMedia(data);
            } else {
                setMedia([]);
            }
        } catch (error) {
            console.error('Error loading media:', error);
            toast.error('Failed to load media');
            setMedia([]);
        } finally {
            setLoading(false);
        }
    };

    // Upload file to Railway file server
    const uploadToFileServer = async (file) => {
        setUploading(true);
        setUploadProgress(0);
        
        try {
            const formData = new FormData();
            formData.append('file', file);

            return new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                
                xhr.upload.addEventListener('progress', (event) => {
                    if (event.lengthComputable) {
                        const percentComplete = (event.loaded / event.total) * 100;
                        setUploadProgress(Math.round(percentComplete));
                    }
                });

                xhr.addEventListener('load', () => {
                    if (xhr.status === 200) {
                        const response = JSON.parse(xhr.responseText);
                        setUploading(false);
                        setUploadProgress(0);
                        resolve(response.fileInfo.fileUrl);
                    } else {
                        reject(new Error(`Upload failed with status ${xhr.status}`));
                    }
                });

                xhr.addEventListener('error', () => {
                    reject(new Error('Upload failed'));
                });

                xhr.open('POST', `${FILE_SERVER_URL}/upload`);
                xhr.send(formData);
            });
        } catch (error) {
            setUploading(false);
            setUploadProgress(0);
            console.error('Upload error:', error);
            throw error;
        }
    };

    // Handle file selection
    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file size (max 100MB)
        const maxSize = 100 * 1024 * 1024; // 100MB in bytes
        if (file.size > maxSize) {
            toast.error('File size exceeds 100MB limit');
            return;
        }

        // Detect media type from file
        const detectedType = detectMediaTypeFromFile(file);
        
        // Generate preview for images
        let filePreview = null;
        if (detectedType === 'image') {
            filePreview = URL.createObjectURL(file);
        }

        setFormData(prev => ({
            ...prev,
            selectedFile: file,
            file_url: '',
            media_type: detectedType,
            filePreview: filePreview,
            title: formData.title || file.name.replace(/\.[^/.]+$/, "") // Auto-set title from filename
        }));
    };

    const detectMediaTypeFromFile = (file) => {
        if (!file) return 'image';
        
        const type = file.type;
        
        if (type.startsWith('image/')) {
            return 'image';
        } else if (type.startsWith('video/')) {
            return 'video';
        } else if (type.startsWith('audio/')) {
            return 'audio';
        } else if (type.includes('pdf') || 
                   type.includes('document') || 
                   type.includes('text') ||
                   file.name.match(/\.(pdf|doc|docx|txt|rtf|xls|xlsx|ppt|pptx)$/i)) {
            return 'document';
        } else {
            return 'other';
        }
    };

    const detectMediaTypeFromUrl = (url) => {
        if (!url) return 'image';
        
        // Check if it's a YouTube URL
        if (isYouTubeUrl(url)) {
            return 'video';
        }
        
        // Check file extension
        const urlLower = url.toLowerCase();
        
        // Image extensions
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
        if (imageExtensions.some(ext => urlLower.includes(ext))) {
            return 'image';
        }
        
        // Video extensions
        const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.wmv', '.flv', '.mkv'];
        if (videoExtensions.some(ext => urlLower.includes(ext))) {
            return 'video';
        }
        
        // Document extensions
        const docExtensions = ['.pdf', '.doc', '.docx', '.txt', '.rtf', '.xls', '.xlsx', '.ppt', '.pptx'];
        if (docExtensions.some(ext => urlLower.includes(ext))) {
            return 'document';
        }
        
        // Default to image for URLs without clear extensions
        return 'image';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setUploading(true);
            
            let fileUrl = formData.file_url;

            // If a file is selected, upload it to the file server
            if (formData.selectedFile) {
                toast.loading('Uploading file to server...');
                fileUrl = await uploadToFileServer(formData.selectedFile);
                toast.dismiss();
                toast.success('File uploaded successfully!');
            }

            // Determine media type
            let mediaType = formData.media_type;
            if (!formData.selectedFile && formData.file_url) {
                mediaType = detectMediaTypeFromUrl(formData.file_url);
            }

            // Prepare data for backend
            const submissionData = {
                title: formData.title,
                description: formData.description,
                file_url: fileUrl,
                media_type: mediaType,
                category: formData.category,
                is_featured: formData.is_featured
            };
            
            console.log('Submitting media data:', submissionData);
            
            if (editingId) {
                await updateMedia(editingId, submissionData);
                toast.success('Media updated successfully!');
            } else {
                await uploadMedia(submissionData);
                toast.success('Media uploaded successfully!');
            }
            
            resetForm();
            await loadMedia();
            
        } catch (error) {
            console.error('Error saving media:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to save media';
            toast.error(errorMessage);
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this media?')) {
            try {
                await deleteMedia(id);
                toast.success('Media deleted successfully!');
                await loadMedia();
            } catch (error) {
                console.error('Error deleting media:', error);
                toast.error('Failed to delete media');
            }
        }
    };

    const handleEdit = (item) => {
        setFormData({
            title: item.title || '',
            description: item.description || '',
            file_url: item.file_url || '',
            media_type: item.media_type || detectMediaTypeFromUrl(item.file_url),
            category: item.category || '',
            is_featured: item.is_featured || false,
            selectedFile: null,
            filePreview: null
        });
        setEditingId(item.id);
        setShowForm(true);
    };

    const resetForm = () => {
        // Clean up preview URL if exists
        if (formData.filePreview) {
            URL.revokeObjectURL(formData.filePreview);
        }
        
        setFormData({
            title: '',
            description: '',
            file_url: '',
            media_type: 'image',
            category: '',
            is_featured: false,
            selectedFile: null,
            filePreview: null
        });
        setEditingId(null);
        setShowForm(false);
        setUploadProgress(0);
    };

    const handleUrlChange = (e) => {
        const url = e.target.value;
        const detectedType = detectMediaTypeFromUrl(url);
        
        setFormData(prev => ({ 
            ...prev, 
            file_url: url,
            media_type: detectedType,
            selectedFile: null,
            filePreview: null
        }));
        
        // Auto-generate title from YouTube video if empty
        if (url && isYouTubeUrl(url) && !formData.title) {
            const videoId = getYouTubeVideoId(url);
            setFormData(prev => ({ 
                ...prev, 
                title: `YouTube Video: ${videoId}` 
            }));
        }
    };

    const handleClearFile = () => {
        if (formData.filePreview) {
            URL.revokeObjectURL(formData.filePreview);
        }
        
        setFormData(prev => ({
            ...prev,
            selectedFile: null,
            filePreview: null
        }));
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const file = e.dataTransfer.files[0];
            
            // Create a fake event object
            const fakeEvent = {
                target: {
                    files: [file]
                }
            };
            
            handleFileSelect(fakeEvent);
        }
    };

    const getYouTubeVideoId = (url) => {
        if (!url) return null;
        
        const patterns = [
            /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i,
            /youtube\.com\/watch\?v=([^"&?\/\s]{11})/i,
            /youtube\.com\/embed\/([^"&?\/\s]{11})/i,
            /youtu\.be\/([^"&?\/\s]{11})/i
        ];
        
        for (const pattern of patterns) {
            const match = url.match(pattern);
            if (match && match[1]) {
                return match[1];
            }
        }
        
        return null;
    };

    const isYouTubeUrl = (url) => {
        return getYouTubeVideoId(url) !== null;
    };

    const renderMediaPreview = (item) => {
        if (item.media_type === 'video' && isYouTubeUrl(item.file_url)) {
            const videoId = getYouTubeVideoId(item.file_url);
            return (
                <div className="relative w-full h-48">
                    <iframe
                        src={`https://www.youtube.com/embed/${videoId}`}
                        title={item.title}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    ></iframe>
                </div>
            );
        } else if (item.media_type === 'video') {
            return (
                <div className="w-full h-48 flex items-center justify-center bg-gray-800">
                    <video 
                        src={item.file_url} 
                        className="w-full h-full object-cover"
                        controls
                        poster="/video-thumbnail.jpg"
                    >
                        Your browser does not support the video tag.
                    </video>
                </div>
            );
        } else if (item.media_type === 'image') {
            return (
                <img 
                    src={item.file_url} 
                    alt={item.title}
                    className="w-full h-48 object-cover"
                    onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/default-media.jpg';
                    }}
                />
            );
        } else if (item.media_type === 'document') {
            return (
                <div className="w-full h-48 flex items-center justify-center bg-blue-50">
                    <div className="text-center">
                        <svg className="w-16 h-16 text-blue-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                        </svg>
                        <p className="text-gray-600 mt-2 text-sm font-medium">Document</p>
                        <p className="text-gray-400 text-xs mt-1">PDF, Word, Excel, etc.</p>
                    </div>
                </div>
            );
        } else {
            return (
                <div className="w-full h-48 flex items-center justify-center bg-gray-100">
                    <div className="text-center">
                        <svg className="w-16 h-16 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                        </svg>
                        <p className="text-gray-600 mt-2 text-sm">File</p>
                        <p className="text-gray-400 text-xs mt-1">{item.media_type || 'Unknown'}</p>
                    </div>
                </div>
            );
        }
    };

    const renderFormPreview = () => {
        if (formData.filePreview) {
            return (
                <img 
                    src={formData.filePreview} 
                    alt="Preview"
                    className="w-full h-48 object-cover rounded border"
                />
            );
        } else if (formData.file_url) {
            return renderMediaPreview(formData);
        } else if (formData.selectedFile) {
            return (
                <div className="w-full h-48 flex items-center justify-center bg-gray-100 rounded border">
                    <div className="text-center">
                        <svg className="w-16 h-16 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                        </svg>
                        <p className="text-gray-600 mt-2 text-sm font-medium">
                            {formData.selectedFile.name}
                        </p>
                        <p className="text-gray-400 text-xs mt-1">
                            {(formData.selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                        </p>
                    </div>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Media Library</h2>
                <button
                    onClick={() => {
                        resetForm();
                        setShowForm(true);
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                    Add Media
                </button>
            </div>

            {showForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white p-6 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold">
                                {editingId ? 'Edit Media' : 'Add New Media'}
                            </h3>
                            <button
                                onClick={resetForm}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                ✕
                            </button>
                        </div>
                        
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Title *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.title}
                                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        required
                                        disabled={uploading}
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Media Type
                                    </label>
                                    <select
                                        value={formData.media_type}
                                        onChange={(e) => setFormData({...formData, media_type: e.target.value})}
                                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        disabled={uploading || !!formData.selectedFile}
                                    >
                                        <option value="image">Image</option>
                                        <option value="video">Video</option>
                                        <option value="document">Document</option>
                                        <option value="audio">Audio</option>
                                        <option value="other">Other</option>
                                    </select>
                                    {(formData.file_url || formData.selectedFile) && (
                                        <p className="text-xs text-gray-500 mt-1">
                                            Detected as: {formData.media_type}
                                        </p>
                                    )}
                                </div>
                                
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        File Upload or URL
                                    </label>
                                    
                                    {/* File Upload Section */}
                                    <div className="mb-4">
                                        <div 
                                            className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors cursor-pointer"
                                            onClick={() => !uploading && fileInputRef.current?.click()}
                                            onDragOver={handleDragOver}
                                            onDrop={handleDrop}
                                        >
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                onChange={handleFileSelect}
                                                accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.rtf,.xls,.xlsx,.ppt,.pptx"
                                                className="hidden"
                                                disabled={uploading}
                                            />
                                            <div className="flex flex-col items-center">
                                                <svg className="w-12 h-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                                                </svg>
                                                <p className="text-sm text-gray-600 mb-2">
                                                    <span className="font-semibold">Click to upload</span> or drag and drop
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    Images, Videos, Documents up to 100MB
                                                </p>
                                            </div>
                                        </div>

                                        {/* Selected File Info */}
                                        {formData.selectedFile && (
                                            <div className="mt-3 p-3 bg-blue-50 rounded flex justify-between items-center">
                                                <div>
                                                    <p className="text-sm font-medium text-gray-700">
                                                        {formData.selectedFile.name}
                                                    </p>
                                                    <p className="text-xs text-gray-500">
                                                        {(formData.selectedFile.size / 1024).toFixed(1)} KB • {formData.media_type}
                                                    </p>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={handleClearFile}
                                                    className="text-red-500 hover:text-red-700"
                                                    disabled={uploading}
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            </div>
                                        )}

                                        {/* Upload Progress */}
                                        {uploading && (
                                            <div className="mt-3 w-full">
                                                <div className="flex justify-between text-sm text-gray-600 mb-1">
                                                    <span>Uploading to Railway...</span>
                                                    <span>{uploadProgress}%</span>
                                                </div>
                                                <div className="w-full bg-gray-200 rounded-full h-2">
                                                    <div 
                                                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                                        style={{ width: `${uploadProgress}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* OR Separator */}
                                    <div className="relative my-4">
                                        <div className="absolute inset-0 flex items-center">
                                            <div className="w-full border-t border-gray-300"></div>
                                        </div>
                                        <div className="relative flex justify-center text-sm">
                                            <span className="px-2 bg-white text-gray-500">OR</span>
                                        </div>
                                    </div>

                                    {/* URL Input */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Enter File URL
                                        </label>
                                        <input
                                            type="url"
                                            value={formData.selectedFile ? '' : formData.file_url}
                                            onChange={handleUrlChange}
                                            className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="https://example.com/file.jpg or https://youtube.com/watch?v=..."
                                            disabled={uploading || !!formData.selectedFile}
                                        />
                                        {formData.file_url && isYouTubeUrl(formData.file_url) && (
                                            <p className="text-green-600 text-sm mt-1 flex items-center">
                                                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
                                                </svg>
                                                YouTube video detected
                                            </p>
                                        )}
                                    </div>
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Category (Optional)
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.category}
                                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="e.g., Promotional, Tutorials, Documents"
                                        disabled={uploading}
                                    />
                                </div>
                                
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Description
                                    </label>
                                    <textarea
                                        value={formData.description}
                                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        rows="3"
                                        disabled={uploading}
                                    />
                                </div>
                            </div>
                            
                            <div className="flex items-center space-x-4">
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id="is_featured"
                                        checked={formData.is_featured}
                                        onChange={(e) => setFormData({...formData, is_featured: e.target.checked})}
                                        className="rounded text-blue-600 focus:ring-blue-500"
                                        disabled={uploading}
                                    />
                                    <label htmlFor="is_featured" className="text-sm text-gray-700">
                                        Featured Media
                                    </label>
                                </div>
                            </div>
                            
                            {/* Preview Section */}
                            {(formData.filePreview || formData.file_url || formData.selectedFile) && (
                                <div className="border rounded-lg p-4 bg-gray-50">
                                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                                        Preview ({formData.media_type}):
                                    </h4>
                                    <div className="h-48">
                                        {renderFormPreview()}
                                    </div>
                                </div>
                            )}
                            
                            <div className="flex justify-end space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={resetForm}
                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50"
                                    disabled={uploading}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={uploading || (!formData.selectedFile && !formData.file_url)}
                                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {uploading ? (
                                        <span className="flex items-center">
                                            <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            {formData.selectedFile ? 'Uploading...' : 'Saving...'}
                                        </span>
                                    ) : editingId ? 'Update Media' : 'Add Media'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="flex justify-center items-center h-64">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="mt-4 text-gray-600">Loading media...</p>
                    </div>
                </div>
            ) : media.length === 0 ? (
                <div className="text-center py-12">
                    <p className="text-gray-500 text-lg">No media files found</p>
                    <p className="text-gray-400 mt-2">Add your first media file to get started</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {media.map(item => (
                        <div key={item.id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                            {/* Media Preview */}
                            <div className="h-48 overflow-hidden bg-gray-100">
                                {renderMediaPreview(item)}
                            </div>
                            
                            {/* Media Info */}
                            <div className="p-4">
                                <div className="flex justify-between items-start mb-2">
                                    <div>
                                        <h3 className="font-semibold text-gray-800 truncate" title={item.title}>
                                            {item.title}
                                        </h3>
                                        <div className="flex items-center space-x-2 mt-1">
                                            {item.is_featured && (
                                                <span className="inline-block px-2 py-1 rounded text-xs bg-yellow-100 text-yellow-800">
                                                    Featured
                                                </span>
                                            )}
                                            <span className={`inline-block px-2 py-1 rounded text-xs ${
                                                item.media_type === 'image' ? 'bg-green-100 text-green-800' :
                                                item.media_type === 'video' ? 'bg-red-100 text-red-800' :
                                                item.media_type === 'document' ? 'bg-blue-100 text-blue-800' :
                                                'bg-gray-100 text-gray-800'
                                            }`}>
                                                {item.media_type}
                                            </span>
                                            {item.category && (
                                                <span className="inline-block px-2 py-1 rounded text-xs bg-gray-100 text-gray-800">
                                                    {item.category}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                
                                {item.description && (
                                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                                        {item.description}
                                    </p>
                                )}
                                
                                <div className="flex justify-between items-center">
                                    <a 
                                        href={item.file_url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-xs text-blue-600 hover:text-blue-800 truncate max-w-[120px]"
                                        title={item.file_url}
                                    >
                                        View File
                                    </a>
                                    
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={() => handleEdit(item)}
                                            className="text-blue-600 hover:text-blue-800 text-sm font-medium px-2 py-1 hover:bg-blue-50 rounded"
                                            title="Edit"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                        </button>

                                        <Link
                                        to={`/admin/media/${item.id}`}
                                        className="text-green-600 hover:text-green-800 text-sm font-medium px-2 py-1 hover:bg-green-50 rounded"
                                        title="View Details"
                                        >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                        </Link>
                                        
                                        <button
                                            onClick={() => handleDelete(item.id)}
                                            className="text-red-600 hover:text-red-800 text-sm font-medium px-2 py-1 hover:bg-red-50 rounded"
                                            title="Delete"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MediaManager;